import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  IconButton,
  MenuItem
} from '@mui/material';
import { areConnectorsCompatible, compatibilityMatrix } from '../services/compatibilityMatrix';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { findConnectorWithSkuCode } from '../services/utilityService';
import ConnectorCompatibilityIndicator from './ConnectorCompatibilityIndicator';
import ConnectorImageZoom from './ConnectorImageZoom';
import CloseIcon from '@mui/icons-material/Close';
import { getAvailableWireTypes, getDefaultWireType, wireTypes } from '../services/wireCompatibilityService';
import { cableImageCaptureService } from '../services/cableImageCaptureService';
import { generateSKU } from '../services/skuService';
import { buildConnectorFamilies, getFamilyDescription } from '../services/connectorFamiliesService';
import { API_ENDPOINTS } from '../config';
import clientImageCaptureService from '../services/clientImageCaptureService';
import html2canvas from 'html2canvas';
import { imageUrlToBase64DataUri } from '../services/utilityService';
import { formatGenderWithXlrHint } from '../utils/connectorDisplayUtils';
// Removed html-to-image import - now using EC2 system for image generation


const connectorPoints = [
  { id: 'E', label: 'E' },
  { id: 'F', label: 'F' },
  { id: 'G', label: 'G' }
];

const lengthLabels = [
  { id: 'A', label: 'A (Overall)' },
  { id: 'B', label: 'B (E to split)' },
  { id: 'C', label: 'C (split to F)' },
  { id: 'D', label: 'D (split to G)' }
];

const defaultLengths = {
  A: 48,  // Overall length
  B: 36,  // E to split - main trunk (make this the visually dominant segment)
  C: 6,   // split to F - reduced to emphasize B
  D: 6    // split to G - reduced to emphasize B
};

// Define connector families for the modal
// Note: These are now built inside the component's useMemo hook

// Compatibility indicator component for connector selection modal
function CompatibilityIndicator({ connector, selectedPoint, allConnectors }) {
  // For Wye cables, all connectors should be compatible with each other
  const otherSelectedConnectors = Object.entries(allConnectors)
    .filter(([point, c]) => point !== selectedPoint && c?.id)
    .map(([point, c]) => c);
  if (otherSelectedConnectors.length === 0) return null;
  // Check compatibility with all other selected connectors
  for (const otherConnector of otherSelectedConnectors) {
    const result = areConnectorsCompatible(otherConnector.id, connector.id);
    if (!result.compatible) {
      return <CompatibilityIndicator result={result} />;
    }
  }
  // If all are compatible, show compatible
  return <CompatibilityIndicator result={{ compatible: true, warning: null, notes: null }} />;
}

// Helper component for connector selection modal
function ConnectorModal({ selectedPoint, connectors, onConnectorSelect, getFilteredConnectors, type, connectorFamilies, findConnectorVariant }) {
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [loading, setLoading] = useState(true);

  // Use wye connector families
  const families = connectorFamilies.wye;

  // Define pairs for Wye cables
  const pairs = { 'E': ['F', 'G'], 'F': ['E'], 'G': ['E'] };
  const pairedPoints = pairs[selectedPoint] || [];
  const pairedConnectors = pairedPoints.map(p => connectors[p]).filter(Boolean);

  // Function to check compatibility for a potential connector
  const checkConnectorCompatibility = (connector) => {
    if (pairedConnectors.length === 0) {
      return { status: 'compatible', message: 'Compatible' };
    }
    
    try {
      // Check compatibility with all paired connectors
      for (const pairedConnector of pairedConnectors) {
        // For the paired connector, we need to consider the configuration variant
        let pairedId = pairedConnector.id;
        
        // If the paired connector has a configuration that's different from 'standard',
        // we need to find the variant connector ID
        if (pairedConnector.configuration && pairedConnector.configuration !== 'standard') {
          const variantConnector = findConnectorVariant(pairedConnector, pairedConnector.configuration);
          if (variantConnector && variantConnector.id) {
            pairedId = variantConnector.id;
          }
        }
        
        const compatibilityResult = areConnectorsCompatible(pairedId, connector.id);
        
        if (!compatibilityResult) {
          return { status: 'incompatible', message: 'Not compatible' };
        }
        
        if (compatibilityResult.compatible === false) {
          return { 
            status: 'incompatible', 
            message: compatibilityResult.reason || 'Incompatible',
            notes: compatibilityResult.details || null
          };
        }
        
        if (compatibilityResult.warning) {
          return { 
            status: 'warning', 
            message: compatibilityResult.warning,
            notes: compatibilityResult.notes 
          };
        }
      }
      
      return { status: 'compatible', message: 'Compatible' };
    } catch (error) {
      console.warn('Error checking compatibility:', error);
      return { status: 'incompatible', message: 'Error checking compatibility' };
    }
  };

  useEffect(() => {
    const loadConnectors = async () => {
      setLoading(true);
      try {
        await getFilteredConnectors(selectedPoint);
      } catch (error) {
        console.error('Error loading filtered connectors:', error);
      } finally {
        setLoading(false);
      }
    };
    loadConnectors();
  }, [selectedPoint, connectors, getFilteredConnectors]);

  // Handle family selection
  const handleFamilySelect = (family) => {
    setSelectedFamily(family.id);
  };

  // Handle connector selection
  const handleConnectorSelect = (connector) => {
    // Map mountType from compatibility matrix to configuration value
    let autoConfiguration = 'standard';
    if (connector.mountType === 'rightangle' || connector.mountType === 'right-angle') {
      autoConfiguration = 'right-angle';
    } else if (connector.mountType === 'panel') {
      autoConfiguration = 'panel-mount';
    } else if (connector.mountType === 'cable') {
      autoConfiguration = 'standard';
    }
    
    // Convert new connector format to old format that the parent expects
    const legacyConnector = {
      id: connector.id,
      label: connector.name,
      gender: connector.gender,
      image: connector.image,
      family: connector.family,
      type: 'wye',
      brand: 'Professional',
      configuration: autoConfiguration, // Use mapped configuration instead of always 'standard'
      skuCode: connector.id,
      description: connector.name,
      mountType: connector.mountType,
      poles: connector.poles,
      purpose: connector.purpose
    };
    
    onConnectorSelect(selectedPoint, legacyConnector);
  };

  // Go back to family selection
  const handleBackToFamilies = () => {
    setSelectedFamily(null);
  };

  const selectedFamilyObj = families.find(f => f.id === selectedFamily);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography>Loading compatible connectors...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxHeight: '50vh', overflowY: 'auto', mt: 1 }}>
      {/* Compatibility explanation when paired connectors exist */}
      {pairedConnectors.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Compatibility Check Active:</strong> Showing compatibility with your selected {pairedPoints.join(', ')} connector(s). 
            Green = Compatible, Yellow = Warning, Red = Incompatible
          </Typography>
        </Alert>
      )}
      
      {!selectedFamily ? (
        // Step 1: Show connector families
        <>
          <Typography variant="body2" color="text.secondary" paragraph>
            Choose the type of connector you need:
          </Typography>
          <Grid container spacing={2}>
            {families.length > 0 ? (
              families.map((family) => (
                <Grid item xs={12} sm={6} key={family.id}>
                  <Paper
                    sx={{
                      cursor: 'pointer',
                      p: 2,
                      height: '100%',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onClick={() => handleFamilySelect(family)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ width: { xs: 36, sm: 48 }, height: { xs: 36, sm: 48 }, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: 4, p: 0.5, overflow: 'hidden' }}>
                        <img
                          src={family.image}
                          alt={family.name}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {family.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {family.description}
                        </Typography>
                        <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                          {family.variants.length} options available
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="body2">
                    No connectors found for type: <strong>wye</strong>. Please check the compatibility matrix configuration.
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </>
      ) : (
        // Step 2: Show specific connectors in selected family
        <>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              variant="body2" 
              color="primary" 
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={handleBackToFamilies}
            >
              ← Back to connector types
            </Typography>
            <Typography variant="body1">
              <strong>{selectedFamilyObj?.name}</strong> - {selectedFamilyObj?.description}
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            {selectedFamilyObj?.variants.map((connector) => {
              // Use the new compatibility check function
              const compatibilityResult = checkConnectorCompatibility(connector);
              const isIncompatible = compatibilityResult?.status === 'incompatible';
              const hasPairedConnectors = pairedConnectors.length > 0;
              
              return (
                <Grid item xs={12} key={connector.id}>
                  <Paper
                    sx={{ 
                      cursor: isIncompatible ? 'not-allowed' : 'pointer', 
                      opacity: isIncompatible ? 0.6 : 1,
                      border: hasPairedConnectors ? (
                        compatibilityResult?.status === 'incompatible' ? '2px solid #f44336' : 
                        compatibilityResult?.status === 'warning' ? '2px solid #ffb300' : 
                        compatibilityResult?.status === 'compatible' ? '2px solid #4caf50' : '1px solid #e0e0e0'
                      ) : '1px solid #e0e0e0',
                      '&:hover': { 
                        boxShadow: isIncompatible ? 1 : 6,
                        transform: isIncompatible ? 'none' : 'translateY(-2px)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onClick={() => !isIncompatible && handleConnectorSelect(connector)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Box sx={{ width: { xs: 36, sm: 48 }, height: { xs: 36, sm: 48 }, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', '& img': { width: '100%', height: '100%', objectFit: 'contain' } }}>
                        <ConnectorImageZoom src={connector.image} alt={connector.name} style={{ width: '100%', height: '100%' }} />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1">
                          {connector.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatGenderWithXlrHint(connector)} • {connector.mountType === 'cable' ? 'Cable Mount' : connector.mountType === 'panel' ? 'Panel Mount' : connector.mountType}
                        </Typography>
                        {isIncompatible && (
                          <Typography variant="caption" color="error.main" sx={{ fontWeight: 'bold' }}>
                            Incompatible with selected connector(s)
                          </Typography>
                        )}
                      </Box>
                      {/* Compatibility Indicator */}
                      {hasPairedConnectors && (
                        <Box sx={{ ml: 2 }}>
                          <ConnectorCompatibilityIndicator result={compatibilityResult} />
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </Box>
  );
}

// Helper function to check if connector is XLR 5-pin
function isXLR5Pin(connector) {
  return connector?.id === 'NC5FXX' || connector?.id === 'NC5MXX';
}

// Get the appropriate image for connectors based on configuration
function getConnectorImage(connector, configuration) {
  // Use the image property directly from the compatibility matrix entry
  // This ensures we're using the centralized source of truth for image paths
  if (!connector?.image) {
    return '/images/coming_soon.png'; // Fallback for missing images
  }
  
  // For most connectors, return the image directly from the compatibility matrix
  // The compatibility matrix already contains the correct image paths
  return connector.image;
}

function WyeConnectorSelection({ value = {}, onChange, onReady, onMissingRequirements, type, onNext, onBack, svgRef = null, onAddToCart, onImageCaptureReady }) {
  // State for showing tooltip notifications
  const [tooltipMessage, setTooltipMessage] = useState('');
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [connectors, setConnectors] = useState(value.connectors || {});
  const [lengths, setLengths] = useState(() => ({ ...defaultLengths, ...(value.lengths || {}) }));
  const [specialInstructions, setSpecialInstructions] = useState(value.specialInstructions || '');
  const [unit, setUnit] = useState('in');
  const [overallInput, setOverallInput] = useState(
    unit === 'ft' 
      ? ((value.lengths?.A || defaultLengths.A) / 12).toFixed(2).replace(/\.00$/, '') 
      : String(value.lengths?.A || defaultLengths.A)
  );
  const [editedFields, setEditedFields] = useState(new Set());
  const [lastManualEdit, setLastManualEdit] = useState(null); // Track which field was last manually edited
  const [tempInputs, setTempInputs] = useState({}); // Track temporary input values during typing
  
  // Compatibility checking state
  const [compatibilityWarnings, setCompatibilityWarnings] = useState({});
  
  // Wire type state for connectors - Wye cables always use PSZ
  const [wireType, setWireType] = useState('PSZ');
  const [availableWireTypes, setAvailableWireTypes] = useState([]);
  
  // SVG pan and zoom state
  const [svgTransform, setSvgTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  
  // Internal SVG ref for wheel event handling
  const localSvgRef = useRef(null);
  const actualSvgRef = svgRef || localSvgRef;

  // Reset to default state only when value prop is completely undefined (when switching cable types)
  useEffect(() => {
    if (value === undefined) {
      console.log('🔄 WyeConnectorSelection: Resetting to default state (cable type switch)');
      setConnectors({});
      setLengths(defaultLengths);
      setSpecialInstructions('');
      setWireType('PSZ');
      setEditedFields(new Set());
      setLastManualEdit(null);
      setOverallInput(String(defaultLengths.A));
      setCompatibilityWarnings({});
      setAvailableWireTypes([]);
      setTempInputs({});
    }
  }, [value === undefined]);

  // Update state when value prop changes (for data persistence)
  useEffect(() => {
    if (value) {
      if (value.connectors) {
        setConnectors(value.connectors);
      }
      if (value.lengths) {
        setLengths(prev => ({ ...prev, ...value.lengths }));
      }
      if (value.specialInstructions !== undefined) {
        setSpecialInstructions(value.specialInstructions);
      }
      if (value.wireType) {
        setWireType(value.wireType);
      }
    }
  }, [value]);

  // Memoize connector families to prevent repeated buildConnectorFamilies calls
  const connectorFamilies = useMemo(() => {
    const wyeConnectorFamilies = buildConnectorFamilies('wye');
    return { wye: wyeConnectorFamilies };
  }, []);

  // Helper function to convert relative paths to absolute URLs for client-side rendering
  const getImageUrlForDisplay = useCallback((imagePath) => {
    if (!imagePath) return imagePath;
    if (imagePath.startsWith('data:') || imagePath.startsWith('http')) {
      return imagePath;
    }
    // Convert relative URL to absolute URL for client-side rendering
    return imagePath.startsWith('/') 
      ? `${window.location.origin}${imagePath}`
      : `${window.location.origin}/${imagePath}`;
  }, []);

  // Memoize connector images to prevent repeated getConnectorImage calls
  const connectorImages = useMemo(() => {
    const images = {};
    ['E', 'F', 'G'].forEach(point => {
      const connector = connectors[point];
      if (connector?.id) {
        const imageUrl = getConnectorImage(connector, connector.configuration || 'standard');
        // Store relative paths directly for consistent Puppeteer capture
        images[point] = imageUrl;
      }
    });
    return images;
  }, [connectors]);

  useEffect(() => {
    // Example logic: update compatibilityResults and other state based on props or selection
    // Replace with actual logic as needed
    if (value.connectors && value.selectedConnectorId) {
      // Removed: const selected = value.connectors.find(c => c.id === value.selectedConnectorId);
      // setSelectedConnector(selected); // <-- REMOVED
      // Example: set pairedConnector and otherConnectors
      // Removed: const others = val
      // ue.connectors.filter(c => c.id !== value.selectedConnectorId);
      // setOtherConnectors(others);
      // Example: set compatibilityResults and selectedCompatibility
      // Replace with actual compatibility logic
      // Removed: setCompatibilityResults([]); // Fill with actual compatibility results
    }
  }, [value.connectors, value.selectedConnectorId]);

  // Wye cables always use PSZ, so no need to update wire types
  useEffect(() => {
    // Always maintain PSZ for wye cables
    if (wireType !== 'PSZ') {
      setWireType('PSZ');
    }
  }, [wireType]);

  // Wye cables always use PSZ, so no need to sync with value prop

  // Check compatibility between selected connectors (all same type - audio/power)
  useEffect(() => {
    const checkCompatibility = () => {
      const warnings = {};
      // Check all bidirectional pairs: E-F, F-E, E-G, G-E
      const pairs = [
        ['E', 'F'],
        ['F', 'E'],
        ['E', 'G'],
        ['G', 'E']
      ];
      for (const [connA_id, connB_id] of pairs) {
        const connA = connectors[connA_id];
        const connB = connectors[connB_id];
        if (connA?.id && connB?.id) {
          try {
            const compatibilityResult = areConnectorsCompatible(connA.id, connB.id);
            if (compatibilityResult && !compatibilityResult.compatible) {
              const key = `${connA_id}-${connB_id}`;
              warnings[key] = {
                type: 'warning',
                message: compatibilityResult.warning || 'Connectors are not compatible',
                details: compatibilityResult.notes
              };
            }
          } catch (error) {
            console.warn('Error checking compatibility:', error);
          }
        }
      }
      setCompatibilityWarnings(warnings);
    };
    checkCompatibility();
  }, [connectors]);

  // --- SVG layout and zoom logic (like hybrid Zip) ---
  // Calculate cable geometry
  const svgWidth = 950, pad = 20;
  const connW = 40, connH = 40;
  const splitY = 200;
  const branchAngle = 35;
  const aLen = parseFloat(lengths['A']) || 0; // Overall length
  const bLen = parseFloat(lengths['B']) || 0; // D to split
  const cLen = parseFloat(lengths['C']) || 0; // split to E
  const dLen = parseFloat(lengths['D']) || 0; // split to F
  
  // Visual scaling for demo (like hybrid components)
  const PX_PER_INCH_VISUAL = 6; // Consistent with other components
  
  // Maximum visual length for SVG display (equivalent to ~50 inches visually)
  const MAX_VISUAL_LENGTH = 300;
  
  // Calculate visual lengths with consistent scaling
  const bLenPx = bLen * PX_PER_INCH_VISUAL;
  const cLenPx = cLen * PX_PER_INCH_VISUAL;
  const dLenPx = dLen * PX_PER_INCH_VISUAL;
  
  // Apply visual length caps for reasonable SVG display
  const bVisual = Math.min(bLenPx, MAX_VISUAL_LENGTH);
  const cVisual = Math.min(cLenPx, MAX_VISUAL_LENGTH);
  const dVisual = Math.min(dLenPx, MAX_VISUAL_LENGTH);
  
  // Use visual lengths for positioning (with minimum for very short cables)
  const mainLenPx = Math.max(240, bVisual);
  const branchLenCPx = Math.max(80, cVisual);
  const branchLenDPx = Math.max(80, dVisual);
  
  // Endpoints
  const leftX = svgWidth * 0.4; // Better centering - position split point closer to center
  const E = { x: leftX - mainLenPx, y: splitY };
  const split = { x: leftX, y: splitY };
  const F = { x: leftX + branchLenCPx * Math.cos(branchAngle * Math.PI / 180), y: splitY - branchLenCPx * Math.sin(branchAngle * Math.PI / 180) };
  const G = { x: leftX + branchLenDPx * Math.cos(branchAngle * Math.PI / 180), y: splitY + branchLenDPx * Math.sin(branchAngle * Math.PI / 180) };
  // Zoom/fit logic
  const allX = [E.x - connW, split.x, F.x + connW, G.x + connW];
  const allY = [
    E.y - connH, 
    split.y, 
    F.y - connH, 
    G.y + connH,
    Math.min(F.y, G.y) - 90, // Overall length label (updated position)
    G.y + 70 // Bottom G label
  ];
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);
  const minY = Math.min(...allY);
  const maxY = Math.max(...allY);
  // Auto-scaling logic - simplified for better centering
  const contentWidth = maxX - minX;
  const contentHeight = maxY - minY;
  
  // Calculate viewBox for proper centering with adequate padding for labels
  const viewBoxWidth = contentWidth + 2 * pad;
  const viewBoxHeight = contentHeight + 2 * pad; // More padding for labels
  const viewBoxX = minX - pad;
  const viewBoxY = minY - pad; // Full padding at top for A label
  
  // No transform needed - let SVG handle centering
  const groupStyle = {
    transition: 'all 0.3s ease',
  };

  // Auto-adjust overall length when branch lengths change and recalculate B
  useEffect(() => {
    // Don't auto-adjust A if the user has manually edited it
    if (lastManualEdit === 'A') {
      // Only recalculate B when A was manually edited
      const newB = calculateLengthB(lengths);
      if (newB !== lengths['B']) {
        setLengths(prev => ({
          ...prev,
          B: newB
        }));
        setEditedFields(prev => new Set([...prev, 'B'])); // Mark B as edited
      }
      return;
    }
    
    // Only auto-adjust A if it's not the last manually edited field
    if (lastManualEdit && lastManualEdit !== 'A') {
      const leftLen = Math.max(parseFloat(lengths['C']) || 0, 0); // Wye cables only have C and D
      const rightLen = Math.max(parseFloat(lengths['D']) || 0, 0); // Wye cables only have C and D
      const minMainLen = 6;
      const requiredOverall = leftLen + rightLen + minMainLen;
      const currentOverall = parseFloat(lengths['A']) || 0;
      
      if (requiredOverall > currentOverall) {
        const newLengths = { ...lengths, A: requiredOverall, B: minMainLen, Overall: requiredOverall };
        setLengths(newLengths);
        setEditedFields(prev => new Set([...prev, 'B'])); // Mark B as edited
        if (onChange) onChange({ connectors, lengths: newLengths, specialInstructions, wireType });
      } else {
        // Recalculate B whenever any length changes using the proper calculation function
        const newB = calculateLengthB(lengths);
        if (newB !== lengths['B']) {
          setLengths(prev => ({
            ...prev,
            B: newB
          }));
          setEditedFields(prev => new Set([...prev, 'B'])); // Mark B as edited
        }
      }
    } else {
      // Recalculate B whenever any length changes using the proper calculation function
      const newB = calculateLengthB(lengths);
      if (newB !== lengths['B']) {
        setLengths(prev => ({
          ...prev,
          B: newB
        }));
        setEditedFields(prev => new Set([...prev, 'B'])); // Mark B as edited
      }
    }
  }, [lengths, connectors, specialInstructions, onChange, lastManualEdit]);

  // Auto-adjust Length A when B changes (only if B was last manually edited)
  useEffect(() => {
    if (bLen > 0 && lastManualEdit === 'B') {
      const maxBranch = Math.max(cLen || defaultLengths.C, dLen || defaultLengths.D);
      const calculatedA = Math.round(bLen + maxBranch);
      if (calculatedA !== aLen) {
        const newLengths = { ...lengths, A: calculatedA, Overall: calculatedA };
        setLengths(newLengths);
        // Don't mark A as edited automatically - user must manually edit it
        // Update the overallInput display value
        const displayValue = unit === 'ft' ? (parseFloat(calculatedA) / 12).toFixed(2).replace(/\.00$/, '') : calculatedA.toString();
        setOverallInput(displayValue);
      }
    }
  }, [bLen, cLen, dLen, aLen, unit, lastManualEdit, lengths]);

  // Auto-adjust Length B when C or D changes (only if C or D was last manually edited)
  useEffect(() => {
    if ((lastManualEdit === 'C' || lastManualEdit === 'D') && aLen > 0) {
      const maxBranch = Math.max(cLen || defaultLengths.C, dLen || defaultLengths.D);
      const calculatedB = Math.round(aLen - maxBranch);
      if (calculatedB > 0 && calculatedB !== bLen) {
        setLengths(prev => ({ ...prev, B: calculatedB }));
        setEditedFields(prev => new Set([...prev, 'B'])); // Mark B as edited
      }
    }
  }, [cLen, dLen, aLen, bLen, lastManualEdit, lengths]);

  // Input handlers
  const handlePointClick = (id) => {
    setSelectedPoint(id);
    setModalOpen(true);
  };
  
  
  // Helper function to map configuration to mount type
  const mapConfigurationToMountType = (configuration) => {
    switch (configuration) {
      case 'standard':
        return 'cable';
      case 'panel-mount':
        return 'panel';
      case 'right-angle':
        return 'rightangle'; // Note: some connectors use 'right-angle', others use 'rightangle'
      default:
        return configuration;
    }
  };

  // Helper function to find the correct connector variant based on configuration
  const findConnectorVariant = useCallback((currentConnector, configuration) => {
    if (!currentConnector || !currentConnector.id) return currentConnector;
    
    // Get the current connector's data from the compatibility matrix
    const currentConnectorData = compatibilityMatrix[currentConnector.id];
    if (!currentConnectorData) {
      return {
        ...currentConnector,
        configuration: configuration
      };
    }
    
    const { family, type, purpose, gender, poles } = currentConnectorData;
    const targetMountType = mapConfigurationToMountType(configuration);
    
    // Search through the compatibility matrix to find the variant with the same family, type, purpose, gender, and poles
    // but with the desired mount type (configuration)
    for (const [connectorId, connectorData] of Object.entries(compatibilityMatrix)) {
      // Handle comma-separated purposes like 'audio, hybrid' or 'audio,hybrid'
      const connectorPurposes = connectorData.purpose ? 
        connectorData.purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      const targetPurposes = purpose ? 
        purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      // Check if any of the connector's purposes match any of the target purposes
      const purposeMatches = connectorPurposes.some(connectorPurpose => 
        targetPurposes.includes(connectorPurpose)
      );
      
      if (connectorData.family === family && 
          connectorData.type === type &&
          purposeMatches &&
          connectorData.gender === gender &&
          connectorData.poles === poles &&
          (connectorData.mountType === targetMountType || 
           (targetMountType === 'rightangle' && connectorData.mountType === 'right-angle'))) {
        
        // Return the complete connector object with the new ID and all updated properties
        const updatedConnector = {
          ...currentConnector,
          id: connectorId,
          skuCode: connectorData.skuCode || connectorId, // Use skuCode from matrix, fallback to id
          configuration: configuration,
          image: connectorData.image || currentConnector.image, // Use new image or fallback to current
          description: connectorData.description || connectorData.name || currentConnector.description,
          gender: connectorData.gender || currentConnector.gender,
          mountType: connectorData.mountType,
          family: connectorData.family || currentConnector.family,
          type: connectorData.type || currentConnector.type,
          purpose: connectorData.purpose || currentConnector.purpose,
          label: connectorData.name || connectorData.description || currentConnector.label
        };
        
        return updatedConnector;
      }
    }
    
    // If no variant found, return the original connector with updated configuration
    return {
      ...currentConnector,
      configuration: configuration
    };
  }, []);

  // Helper function to get available configurations for a specific connector
  const getConfigurationsForConnector = useCallback((connector) => {
    if (!connector?.id) return { standard: true, 'right-angle': true, 'panel-mount': true };
    
    // Get the connector data from the compatibility matrix
    const connectorData = compatibilityMatrix[connector.id];
    if (!connectorData) {
      return { standard: true, 'right-angle': true, 'panel-mount': true };
    }
    
    const { family, type, purpose, gender, poles } = connectorData;
    
    // Search through the compatibility matrix to find all variants with the same family, type, purpose, gender, AND poles
    const availableMountTypes = new Set();
    
    Object.values(compatibilityMatrix).forEach(connector => {
      // Handle comma-separated purposes like 'audio, hybrid' or 'audio,hybrid'
      const connectorPurposes = connector.purpose ? 
        connector.purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      const targetPurposes = purpose ? 
        purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      // Check if any of the connector's purposes match any of the target purposes
      const purposeMatches = connectorPurposes.some(connectorPurpose => 
        targetPurposes.includes(connectorPurpose)
      );
      
      // For wye builds, we need to ensure the variant has 'wye' in its purpose
      const hasWyePurpose = connectorPurposes.includes('wye');
      
      if (connector.family === family && 
          connector.type === type && 
          purposeMatches &&
          hasWyePurpose && // Ensure variant has 'wye' purpose for wye builds
          connector.gender === gender &&
          connector.poles === poles) {
        availableMountTypes.add(connector.mountType);
      }
    });

    // Map mountType values to configuration names
    const hasStandard = availableMountTypes.has('cable');
    const hasRightAngle = availableMountTypes.has('rightangle') || availableMountTypes.has('right-angle');
    const hasPanelMount = availableMountTypes.has('panel');

    return {
      standard: hasStandard,
      'right-angle': hasRightAngle,
      'panel-mount': hasPanelMount
    };
  }, []);

  // Helper function to check if a configuration would be incompatible with the paired connector
  const checkConfigurationCompatibility = useCallback((pointId, configuration) => {
    const currentConnector = connectors[pointId];
    if (!currentConnector?.id) return null;
    
    // First check if this configuration actually exists for this connector
    const currentConnectorData = compatibilityMatrix[currentConnector.id];
    if (!currentConnectorData) return null;
    
    const { family, type, purpose, poles } = currentConnectorData;
    const targetMountType = mapConfigurationToMountType(configuration);
    
    // Check if a variant with this configuration actually exists
    let variantExists = false;
    for (const [, connectorData] of Object.entries(compatibilityMatrix)) {
      // Handle comma-separated purposes like 'audio, hybrid' or 'audio,hybrid'
      const connectorPurposes = connectorData.purpose ? 
        connectorData.purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      const targetPurposes = purpose ? 
        purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      // Check if any of the connector's purposes match any of the target purposes
      const purposeMatches = connectorPurposes.some(connectorPurpose => 
        targetPurposes.includes(connectorPurpose)
      );
      
      // For wye builds, we need to ensure the variant has 'wye' in its purpose
      const hasWyePurpose = connectorPurposes.includes('wye');
      
      if (connectorData.family === family && 
          connectorData.type === type &&
          purposeMatches &&
          hasWyePurpose && // Ensure variant has 'wye' purpose for wye builds
          connectorData.poles === poles &&
          (connectorData.mountType === targetMountType || 
           (targetMountType === 'rightangle' && connectorData.mountType === 'right-angle'))) {
        variantExists = true;
        break;
      }
    }
    
    // If the configuration doesn't exist, return null (will be disabled, not red)
    if (!variantExists) return null;
    
    // Find the connector variant for this configuration
    const newConnector = findConnectorVariant(currentConnector, configuration);
    if (!newConnector) return null;
    
    // Check compatibility with the paired connectors
    const pairs = { 'E': ['F', 'G'], 'F': ['E'], 'G': ['E'] };
    const pairedPoints = pairs[pointId] || [];
    
    for (const pairedPoint of pairedPoints) {
      const pairedConnector = connectors[pairedPoint];
      if (pairedConnector?.id) {
        // For the paired connector, we need to consider the configuration variant
        let pairedId = pairedConnector.id;
        
        // If the paired connector has a configuration that's different from 'standard',
        // we need to find the variant connector ID
        if (pairedConnector.configuration && pairedConnector.configuration !== 'standard') {
          const variantConnector = findConnectorVariant(pairedConnector, pairedConnector.configuration);
          if (variantConnector && variantConnector.id) {
            pairedId = variantConnector.id;
          }
        }
        
        const compatResult = areConnectorsCompatible(pairedId, newConnector.id);
        
        if (!compatResult) {
          // If areConnectorsCompatible returns null, it means no compatibility rule exists = incompatible
          return {
            incompatible: true,
            reason: 'Not compatible with paired connector',
            details: null
          };
        }
        
        if (compatResult && !compatResult.compatible) {
          return {
            incompatible: true,
            reason: compatResult.reason || compatResult.message || 'Incompatible with paired connector',
            details: compatResult.details || null
          };
        }
      }
    }
    
    return { incompatible: false };
  }, [connectors, findConnectorVariant]);

  // Filter connectors based on compatibility with already selected connectors
  const getFilteredConnectors = useCallback(async (forPoint) => {
    const families = connectorFamilies.wye;
    const pairs = { 'E': ['F', 'G'], 'F': ['E'], 'G': ['E'] };
    const pairedPoints = pairs[forPoint] || [];
    const pairedConnectors = pairedPoints.map(p => connectors[p]).filter(Boolean);
    
    if (pairedConnectors.length === 0) return families;
    
    // Filter families to only include compatible connectors
    const filteredFamilies = families.map(family => {
      const compatibleVariants = family.variants.filter(connector => {
        try {
          // Check compatibility with all paired connectors
          for (const pairedConnector of pairedConnectors) {
            // For the paired connector, we need to consider the configuration variant
            let pairedId = pairedConnector.id;
            
            // If the paired connector has a configuration that's different from 'standard',
            // we need to find the variant connector ID
            if (pairedConnector.configuration && pairedConnector.configuration !== 'standard') {
              const variantConnector = findConnectorVariant(pairedConnector, pairedConnector.configuration);
              if (variantConnector && variantConnector.id) {
                pairedId = variantConnector.id;
              }
            }
            
            const compatibilityResult = areConnectorsCompatible(pairedId, connector.id);
            if (!compatibilityResult || !compatibilityResult.compatible) {
              return false;
            }
          }
          return true;
        } catch (error) {
          console.warn('Error checking compatibility:', error);
          return false;
        }
      });
      
      return {
        ...family,
        variants: compatibleVariants
      };
    }).filter(family => family.variants.length > 0);
    
    return filteredFamilies;
  }, [connectors, connectorFamilies, findConnectorVariant]);

  const handleConnectorSelect = (pointId, connector) => {
    const skuCode = findConnectorWithSkuCode(connector);
    
    // Map mountType from compatibility matrix to configuration value
    let autoConfiguration = 'standard';
    if (connector.mountType === 'rightangle' || connector.mountType === 'right-angle') {
      autoConfiguration = 'right-angle';
    } else if (connector.mountType === 'panel') {
      autoConfiguration = 'panel-mount';
    } else if (connector.mountType === 'cable') {
      autoConfiguration = 'standard';
    }
    
    setConnectors(prev => {
      const newConnectors = {
        ...prev,
        [pointId]: { 
          ...connector, 
          id: connector.id, 
          skuCode, 
          configuration: autoConfiguration,
          // Ensure all required fields are present for review page
          label: connector.label,
          type: connector.type,
          gender: connector.gender,
          family: connector.family,
          image: connector.image
        }
      };
      
      // Call onChange with the updated connectors
      if (onChange) onChange({ connectors: newConnectors, lengths, specialInstructions, wireType });
      
      return newConnectors;
    });
    
    setSelectedPoint(null);
    setModalOpen(false); // Auto-close the modal
  };

  // Helper: clear connector for a specific point
  const handleClearConnector = useCallback((pointId) => {
    setConnectors(prev => {
      const updated = { ...prev };
      delete updated[pointId];
      
      // Call onChange with the updated connectors
      if (onChange) onChange({ connectors: updated, lengths, specialInstructions, wireType });
      
      return updated;
    });
  }, [lengths, specialInstructions, wireType, onChange]);

  const handleConfigurationChange = (pointId, configuration, event) => {
    const currentConnector = connectors[pointId];
    if (!currentConnector) return;
    
    // Check compatibility before making the change
    const compatibilityCheck = checkConfigurationCompatibility(pointId, configuration);
    if (compatibilityCheck?.incompatible) {
      const message = compatibilityCheck.reason || 'Incompatible with paired connector';
      setTooltipMessage(`Configuration change prevented: ${message}${compatibilityCheck.details ? ` - ${compatibilityCheck.details}` : ''}`);
      setTooltipAnchor(event?.currentTarget);
      setTooltipOpen(true);
      
      // Auto-hide tooltip after 3 seconds
      setTimeout(() => {
        setTooltipOpen(false);
      }, 3000);
      
      return; // Don't update the state
    }
    
    // Find the correct connector variant for this configuration
    const newConnector = findConnectorVariant(currentConnector, configuration);
    
    const newConnectors = {
      ...connectors,
      [pointId]: newConnector
    };
    
    setConnectors(newConnectors);
    
            if (onChange) onChange({ connectors: newConnectors, lengths, specialInstructions, wireType });
  };

  const handleLengthFocus = (id) => {
    setEditedFields(prev => new Set([...prev, id]));
    // Initialize temp input with current display value
    const currentVal = unit === 'ft' ? (parseFloat(lengths[id] || 0) / 12).toFixed(2).replace(/\.00$/, '') : lengths[id] || '';
    setTempInputs(prev => ({ ...prev, [id]: String(currentVal) }));
  };

  const handleLengthBlur = (id) => {
    commitLengthInput(id);
  };

  const handleLengthChange = (id, val) => {
    // Just store the temporary input value without rounding
    setTempInputs(prev => ({ ...prev, [id]: val }));
  };

  const commitLengthInput = (id) => {
    const val = tempInputs[id] || '';
    let v = parseFloat(val);
    if (isNaN(v) || v < 0) v = '';
    else {
      if (unit === 'ft') {
        // Input is in feet, convert to inches for internal storage
        // Cap branch lengths (C, D) at 10 feet, overall length (A) at 500 feet
        if (id === 'A') {
          if (v > 500) v = 500;
        } else if (['C', 'D'].includes(id)) {
          if (v > 10) v = 10;
        } else {
          if (v > 500) v = 500;
        }
        v = v * 12; // Convert feet to inches
        // Round to nearest whole inch
        v = Math.round(v);
      } else {
        // Input is in inches, store directly
        // Round to nearest whole inch
        v = Math.round(v);
        // Cap branch lengths (C, D) at 120 inches (10 feet), overall length (A) at 6000 inches (500 feet)
        if (id === 'A') {
          if (v > 6000) v = 6000;
        } else if (['C', 'D'].includes(id)) {
          if (v > 120) v = 120;
        } else {
          if (v > 6000) v = 6000;
        }
      }
    }
    const newLengths = { ...lengths, [id]: v };
    
    // If C or D was changed, check if A needs to be increased
    let updateA = false;
    if (['C', 'D'].includes(id)) {
      const longerCE = Math.max(parseFloat(newLengths['C'] || 0), 0);
      const longerDF = Math.max(parseFloat(newLengths['D'] || 0), 0);
      const minB = 6; // Minimum B length
      const minRequiredOverall = longerCE + longerDF + minB;
      
      if (newLengths['A'] < minRequiredOverall) {
        newLengths['A'] = minRequiredOverall;
        newLengths['Overall'] = minRequiredOverall;
        updateA = true;
      }
    }
    
    setLengths(newLengths);
    setEditedFields(prev => new Set([...prev, id]));
    setLastManualEdit(id); // Track this as a manual edit
    setTempInputs(prev => {
      const newTemp = { ...prev };
      delete newTemp[id]; // Clear temp input after committing
      return newTemp;
    });
    
    // Update the overallInput field if A was automatically increased
    if (updateA) {
      setEditedFields(prev => new Set([...prev, 'A']));
      const displayValue = unit === 'ft' ? (parseFloat(newLengths['A']) / 12).toFixed(2).replace(/\.00$/, '') : newLengths['A'].toString();
      setOverallInput(displayValue);
    }
    
    if (onChange) onChange({ connectors, lengths: newLengths, specialInstructions, wireType });
  };
  const handleInstructionsChange = (e) => {
    setSpecialInstructions(e.target.value);
    if (onChange) onChange({ connectors, lengths, specialInstructions: e.target.value, wireType });
  };

  // Wye cables always use PSZ, so no wire type change handler needed
  const handleUnitChange = (event, newUnit) => { 
    if (newUnit) {
      setUnit(newUnit);
      // Update the overallInput display value based on the new unit
      const currentLengthA = lengths['A'] || 0;
      if (newUnit === 'ft') {
        setOverallInput((currentLengthA / 12).toFixed(2).replace(/\.00$/, ''));
      } else {
        setOverallInput(String(currentLengthA));
      }
    }
  };

  // SVG pan and zoom handlers
  // Handle wheel events with proper passive listener
  useEffect(() => {
    const svgElement = actualSvgRef?.current;
    if (!svgElement) return;

    const handleWheel = (e) => {
      // Only handle wheel events if we're not over a form element
      const target = e.target;
      const isFormElement = target.closest('input, textarea, select, button, [role="button"]');
      
      if (isFormElement) {
        return; // Let the form element handle the event
      }
      
      // Check if this is a trackpad gesture (deltaY is small) vs mouse wheel (deltaY is large)
      const isTrackpadGesture = Math.abs(e.deltaY) < 50;
      
      if (isTrackpadGesture && e.ctrlKey) {
        // Ctrl + trackpad = zoom towards mouse pointer
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        // Reduce zoom sensitivity by 50% (multiply by 0.5)
        const sensitivityMultiplier = 0.5;
        const adjustedDelta = 1 + (delta - 1) * sensitivityMultiplier;
        const newScale = Math.max(0.5, Math.min(6, svgTransform.scale * adjustedDelta));
        
        // Get mouse position relative to SVG
        const svgRect = e.currentTarget.getBoundingClientRect();
        const mouseX = e.clientX - svgRect.left;
        const mouseY = e.clientY - svgRect.top;
        
        // Calculate zoom center (mouse position or SVG center if mouse is outside)
        const centerX = mouseX >= 0 && mouseX <= svgRect.width ? mouseX : svgRect.width / 2;
        const centerY = mouseY >= 0 && mouseY <= svgRect.height ? mouseY : svgRect.height / 2;
        
        // Calculate new transform to zoom towards the center point
        const scaleChange = newScale / svgTransform.scale;
        const newX = svgTransform.x - (centerX - svgTransform.x) * (scaleChange - 1);
        const newY = svgTransform.y - (centerY - svgTransform.y) * (scaleChange - 1);
        
        setSvgTransform({
          x: newX,
          y: newY,
          scale: newScale
        });
      } else if (!isTrackpadGesture) {
        // Mouse wheel = zoom towards mouse pointer
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newScale = Math.max(0.5, Math.min(6, svgTransform.scale * delta));
        
        // Get mouse position relative to SVG
        const svgRect = e.currentTarget.getBoundingClientRect();
        const mouseX = e.clientX - svgRect.left;
        const mouseY = e.clientY - svgRect.top;
        
        // Calculate zoom center (mouse position or SVG center if mouse is outside)
        const centerX = mouseX >= 0 && mouseX <= svgRect.width ? mouseX : svgRect.width / 2;
        const centerY = mouseY >= 0 && mouseY <= svgRect.height ? mouseY : svgRect.height / 2;
        
        // Calculate new transform to zoom towards the center point
        const scaleChange = newScale / svgTransform.scale;
        const newX = svgTransform.x - (centerX - svgTransform.x) * (scaleChange - 1);
        const newY = svgTransform.y - (centerY - svgTransform.y) * (scaleChange - 1);
        
        setSvgTransform({
          x: newX,
          y: newY,
          scale: newScale
        });
      }
      // For trackpad without Ctrl, let the browser handle scrolling
    };

    // Add event listener with passive: false to allow preventDefault
    svgElement.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      svgElement.removeEventListener('wheel', handleWheel);
    };
  }, [actualSvgRef]);

  const handleSvgMouseDown = (e) => {
    // Only start panning if we're not over a form element
    const target = e.target;
    const isFormElement = target.closest('input, textarea, select, button, [role="button"]');
    
    if (isFormElement) {
      return;
    }
    
    // Only start panning on left mouse button
    if (e.button === 0) {
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
    }
  };

  const handleSvgMouseMove = useCallback((e) => {
    if (!isPanning) return;
    
    // Prevent default browser behavior to ensure smooth panning
    e.preventDefault();
    
    // Only pan if we're not over a form element
    const target = e.target;
    const isFormElement = target.closest('input, textarea, select, button, [role="button"]');
    
    if (isFormElement) {
      setIsPanning(false);
      return;
    }
    
    const deltaX = e.clientX - lastPanPoint.x;
    const deltaY = e.clientY - lastPanPoint.y;
    
    // Enhanced panning for trackpad gestures
    // Trackpad events often have different movement characteristics
    const isTrackpadGesture = e.movementX !== undefined && e.movementY !== undefined;
    
    let adjustedDeltaX = deltaX;
    let adjustedDeltaY = deltaY;
    
    // For trackpad gestures, use movementX/Y if available for more responsive panning
    if (isTrackpadGesture && (e.movementX !== 0 || e.movementY !== 0)) {
      adjustedDeltaX = e.movementX;
      adjustedDeltaY = e.movementY;
    }
    
    setSvgTransform(prev => ({
      ...prev,
      x: prev.x + adjustedDeltaX,
      y: prev.y + adjustedDeltaY
    }));
    setLastPanPoint({ x: e.clientX, y: e.clientY });
  }, [isPanning, lastPanPoint]);

  const handleSvgMouseUp = () => {
    setIsPanning(false);
  };

  // Handle touch events for mobile
  const handleSvgTouchStart = (e) => {
    if (e.touches.length === 1) {
      // Single touch - start panning
      const touch = e.touches[0];
      setIsPanning(true);
      setLastPanPoint({ x: touch.clientX, y: touch.clientY });
    } else if (e.touches.length === 2) {
      // Two finger touch - prevent default to avoid browser zoom
      e.preventDefault();
    }
  };

  const handleSvgTouchMove = useCallback((e) => {
    if (e.touches.length === 1 && isPanning) {
      // Single touch panning - prevent default to ensure smooth panning
      e.preventDefault();
      const touch = e.touches[0];
      const deltaX = touch.clientX - lastPanPoint.x;
      const deltaY = touch.clientY - lastPanPoint.y;
      
      // Enhanced sensitivity for touch panning
      // Touch events may need different sensitivity than mouse events
      const sensitivity = 1.0; // Can be adjusted if needed
      
      setSvgTransform(prev => ({
        ...prev,
        x: prev.x + (deltaX * sensitivity),
        y: prev.y + (deltaY * sensitivity)
      }));
      setLastPanPoint({ x: touch.clientX, y: touch.clientY });
    } else if (e.touches.length === 2) {
      // Two finger pinch zoom towards center of pinch
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      
      // Calculate center of pinch gesture
      const pinchCenterX = (touch1.clientX + touch2.clientX) / 2;
      const pinchCenterY = (touch1.clientY + touch2.clientY) / 2;
      
      // Get SVG bounds
      const svgRect = e.currentTarget.getBoundingClientRect();
      const centerX = pinchCenterX - svgRect.left;
      const centerY = pinchCenterY - svgRect.top;
      
      if (e.scale) {
        // Reduce zoom sensitivity by 50% (multiply by 0.5)
        const sensitivityMultiplier = 0.5;
        const adjustedScale = 1 + (e.scale - 1) * sensitivityMultiplier;
        const newScale = Math.max(0.5, Math.min(6, svgTransform.scale * adjustedScale));
        
        // Calculate new transform to zoom towards the pinch center
        const scaleChange = newScale / svgTransform.scale;
        const newX = svgTransform.x - (centerX - svgTransform.x) * (scaleChange - 1);
        const newY = svgTransform.y - (centerY - svgTransform.y) * (scaleChange - 1);
        
        setSvgTransform({
          x: newX,
          y: newY,
          scale: newScale
        });
      }
    }
  }, [isPanning, lastPanPoint, svgTransform]);

  const handleSvgTouchEnd = () => {
    setIsPanning(false);
  };

  // Reset SVG transform when lengths change
  useEffect(() => {
    setSvgTransform({ x: 0, y: 0, scale: 1 });
  }, [lengths]);


  // Auto-update Overall input field when calculated overall length changes
  useEffect(() => {
    // Don't auto-update if the user has manually edited A
    if (lastManualEdit === 'A') {
      return;
    }
    
    // Only update if the calculated length is significantly different from what's displayed
    const displayValue = unit === 'ft' ? (parseFloat(aLen) / 12).toFixed(2).replace(/\.00$/, '') : aLen.toString();
    const currentInput = parseFloat(overallInput);
    const calculatedValue = parseFloat(displayValue);
    
    // Convert currentInput to the same unit as calculatedValue for comparison
    const currentInputInSameUnit = unit === 'ft' ? currentInput * 12 : currentInput;
    
    // Only update if the difference is more than 1 unit (to avoid minor rounding issues)
    if (!isNaN(currentInput) && !isNaN(calculatedValue) && Math.abs(currentInputInSameUnit - aLen) > 1) {
      setOverallInput(displayValue);
    } else if (isNaN(currentInput) && aLen > 0) {
      // If current input is invalid, update to calculated value
      setOverallInput(displayValue);
    }
  }, [aLen, unit, overallInput, lastManualEdit]);

  // Generate straight wire path for center section
  const generateStraightWire = (startX, endX, centerY, cableLength) => {
    // Simple straight line from start to end
    return `M ${startX} ${centerY} L ${endX} ${centerY}`;
  };
  
  // Calculate length B based on A and branch lengths
  const calculateLengthB = (lengths) => {
    // B = A - (longer of C + longer of D), with minimum of 6"
    const maxBranch = Math.max(parseFloat(lengths['C'] || 0), parseFloat(lengths['D'] || 0));
    const calculatedB = parseFloat(lengths['A'] || 0) - maxBranch;
    return Math.max(6, calculatedB); // Minimum 6" for B
  };
  
  // Auto-adjust Length B when A changes (only if A was last manually edited)
  useEffect(() => {
    if (aLen > 0 && lastManualEdit === 'A') {
      const maxBranch = Math.max(cLen || defaultLengths.C, dLen || defaultLengths.D);
      const calculatedB = Math.round(aLen - maxBranch);
      if (calculatedB > 0 && calculatedB !== bLen) {
        setLengths(prev => ({ ...prev, B: calculatedB }));
        setEditedFields(prev => new Set([...prev, 'B'])); // Mark B as edited
      }
    }
  }, [aLen, cLen, dLen, bLen, lastManualEdit, lengths]);

  const handleOverallInputChange = (val) => {
    // Allow free typing without validation - only store the input value
    setOverallInput(val);
  };
  
  const commitOverallInput = () => {
    let v = parseFloat(overallInput);
    if (isNaN(v) || v < 0) v = 0;
    if (unit === 'ft') {
      // Cap at 500 feet before conversion
      if (v > 500) v = 500;
      v = v * 12; // Convert feet to inches
      // Round to nearest whole inch
      v = Math.round(v);
    } else {
      // Cap at 6000 inches (500 feet)
      if (v > 6000) v = 6000;
      // Round to nearest whole inch
      v = Math.round(v);
    }
    
    // Calculate the minimum required overall length based on branch lengths
    const longerCE = Math.max(parseFloat(lengths['C'] || 0), 0); // Wye cables only have C and D
    const longerDF = Math.max(parseFloat(lengths['D'] || 0), 0); // Wye cables only have C and D
    const minB = 6; // Minimum B length
    const minRequiredOverall = longerCE + longerDF + minB;
    
    // Ensure overall length is never less than minimum required
    if (v < minRequiredOverall) {
      v = minRequiredOverall;
      // Update the input field to show the corrected value
      const displayValue = unit === 'ft' ? (parseFloat(v) / 12).toFixed(2).replace(/\.00$/, '') : v.toString();
      setOverallInput(displayValue);
    }
    
    const newLengths = { ...lengths, A: v, Overall: v };
    setLengths(newLengths);
    setEditedFields(prev => new Set([...prev, 'A'])); // Mark A as edited when committed
    setLastManualEdit('A'); // Track this as a manual edit of A
    
    // Update the overallInput display value to reflect the rounded value
    const displayValue = unit === 'ft' ? (parseFloat(v) / 12).toFixed(2).replace(/\.00$/, '') : v.toString();
    setOverallInput(displayValue);
    
            if (onChange) onChange({ connectors, lengths: newLengths, specialInstructions, wireType });
  };

  const handleReset = () => {
    setLengths(defaultLengths);
    setConnectors({});
    setSpecialInstructions('');
    setEditedFields(new Set()); // Clear all edited fields - user must manually edit again
    setLastManualEdit(null); // Reset manual edit tracking
    setUnit('in');
    setOverallInput(String(defaultLengths.A));
    // Reset capture tracking
    hasCapturedRef.current = false;
    lastConfigRef.current = '';
            if (onChange) onChange({
          connectors: {},
          lengths: defaultLengths,
          specialInstructions: '',
          wireType: 'PSZ'
        });
  };


  // Add ref to track if we've already captured to prevent repeated captures
  const hasCapturedRef = useRef(false);
  const lastConfigRef = useRef('');

  // Debounce special instructions to prevent excessive API calls
  const [debouncedSpecialInstructions, setDebouncedSpecialInstructions] = useState(specialInstructions);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSpecialInstructions(specialInstructions);
    }, 1000); // Wait 1 second after user stops typing
    
    return () => clearTimeout(timer);
  }, [specialInstructions]);

  // Use only lengths and connectors for auto-capture (special instructions don't impact image)
  const currentConfig = JSON.stringify({
    lengths,
    connectors,
    wireType,
    unit
  });

  // Function to prepare SVG for Puppeteer capture by converting image paths to absolute URLs
  const prepareSvgForPuppeteer = useCallback((svgContent) => {
    try {
      // Create a temporary DOM element to parse the SVG
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = svgContent;
      const svgElement = tempDiv.querySelector('svg');
      
      if (!svgElement) {
        console.warn('⚠️ [SVG-PREP] No SVG element found in content');
        return svgContent;
      }
      
      // Find all image elements in the SVG
      const imageElements = svgElement.querySelectorAll('image');
      console.log(`🔄 [SVG-PREP] Found ${imageElements.length} image elements to process`);
      
      // Process each image element
      for (const img of imageElements) {
        const href = img.getAttribute('href');
        if (href) {
          console.log(`🔄 [SVG-PREP] Processing image: ${href}`);
          
          // Convert to absolute URL
          let absoluteUrl;
          if (href.startsWith('data:')) {
            // Keep data URLs as is
            absoluteUrl = href;
            console.log(`🔄 [SVG-PREP] Keeping data URL as is`);
          } else if (href.startsWith('http')) {
            // Already absolute URL
            absoluteUrl = href;
            console.log(`🔄 [SVG-PREP] Already absolute URL: ${absoluteUrl}`);
          } else {
            // Convert relative path to absolute URL
            const baseUrl = 'https://nlfx-cable.luknai.com';
            absoluteUrl = href.startsWith('/') 
              ? `${baseUrl}${href}`
              : `${baseUrl}/${href}`;
            console.log(`🔄 [SVG-PREP] Converted relative path to absolute URL: ${href} → ${absoluteUrl}`);
          }
          
          // Update the href attribute
          img.setAttribute('href', absoluteUrl);
        }
        
        // Remove shadow filter from image elements for Puppeteer capture
        // This can interfere with image rendering in Puppeteer
        if (img.getAttribute('filter')) {
          console.log(`🔄 [SVG-PREP] Removing filter from image element for Puppeteer capture`);
          img.removeAttribute('filter');
        }
      }
      
      // Return the modified SVG content
      return svgElement.outerHTML;
      
    } catch (error) {
      console.error('❌ [SVG-PREP] Error preparing SVG for Puppeteer:', error);
      return svgContent; // Return original content if processing fails
    }
  }, []);

  // Function to convert SVG image URLs to base64 data URIs
  const convertSvgImagesToBase64 = useCallback(async (svgContent) => {
    try {
      // Create a temporary DOM element to parse the SVG
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = svgContent;
      const svgElement = tempDiv.querySelector('svg');
      
      if (!svgElement) {
        console.warn('⚠️ [BASE64-CONVERSION] No SVG element found in content');
        return svgContent;
      }
      
      // Find all image elements in the SVG
      const imageElements = svgElement.querySelectorAll('image');
      console.log(`🔄 [BASE64-CONVERSION] Found ${imageElements.length} image elements to convert`);
      
      // Convert each image to base64
      for (const img of imageElements) {
        const href = img.getAttribute('href');
        if (href && !href.startsWith('data:')) {
          try {
            console.log(`🔄 [BASE64-CONVERSION] Converting image: ${href}`);
            const base64DataUri = await imageUrlToBase64DataUri(href);
            if (base64DataUri) {
              img.setAttribute('href', base64DataUri);
              console.log(`✅ [BASE64-CONVERSION] Successfully converted: ${href.substring(0, 50)}...`);
            } else {
              console.warn(`⚠️ [BASE64-CONVERSION] Failed to convert image: ${href}`);
            }
          } catch (error) {
            console.error(`❌ [BASE64-CONVERSION] Error converting image ${href}:`, error);
          }
        }
      }
      
      // Return the modified SVG content
      return svgElement.outerHTML;
      
    } catch (error) {
      console.error('❌ [BASE64-CONVERSION] Error converting SVG images to base64:', error);
      return svgContent; // Return original content if conversion fails
    }
  }, []);

  // Puppeteer-based image capture function
  const captureWithPuppeteer = useCallback(async (sku) => {
    console.log('🔄 [PUPPETEER-CAPTURE] Starting Puppeteer-based capture...');
    
    try {
      // Generate unique filename
      const timestamp = Date.now();
      const outputFileName = `${sku}_${timestamp}.png`;
      
      // Get the SVG content from the current DOM
      const svgElement = document.getElementById('wye-cable-svg');
      if (!svgElement) {
        console.error('❌ [PUPPETEER-CAPTURE] SVG element not found in DOM');
        return null;
      }
      
      // Prepare SVG for Puppeteer capture by converting image paths to relative paths
      console.log('🔄 [PUPPETEER-CAPTURE] Preparing SVG for Puppeteer capture...');
      const svgWithRelativePaths = await prepareSvgForPuppeteer(svgElement.outerHTML);
      
      console.log('📄 [PUPPETEER-CAPTURE] SVG content length:', svgWithRelativePaths.length);
      
      // Call the server-side capture endpoint with SVG content
      console.log('📡 [PUPPETEER-CAPTURE] Sending request to:', 'https://nlfx-cable.luknai.com/api/capture-svg');
      console.log('📡 [PUPPETEER-CAPTURE] Request payload:', {
        svgContent: svgWithRelativePaths.substring(0, 200) + '...', // Log first 200 chars for debugging
        outputFileName: outputFileName
      });
      
      const response = await fetch('https://nlfx-cable.luknai.com/api/capture-svg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          svgContent: svgWithRelativePaths,
          outputFileName: outputFileName
        })
      });
      
      console.log('📡 [PUPPETEER-CAPTURE] Response status:', response.status);
      console.log('📡 [PUPPETEER-CAPTURE] Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('📡 [PUPPETEER-CAPTURE] Error response body:', errorText);
        throw new Error(`Server responded with ${response.status}: ${response.statusText} - ${errorText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        console.log('✅ [PUPPETEER-CAPTURE] Puppeteer capture successful:', result.s3Url);
        return result.s3Url;
      } else {
        throw new Error(result.error || 'Unknown error from server');
      }
      
    } catch (error) {
      console.error('❌ [PUPPETEER-CAPTURE] Error during Puppeteer capture:', error);
      return null;
    }
  }, []);

  // Client-side image capture function (fallback)
  const captureClientSideImage = useCallback(async () => {
    if (!actualSvgRef?.current) {
      console.log('⚠️ [CLIENT-CAPTURE] No SVG ref available');
      return null;
    }

    try {
      console.log('🔄 [CLIENT-CAPTURE] Capturing client-side image...');
      
      const svgElement = actualSvgRef.current;
      
      // Get the SVG's explicit width and height attributes
      const svgWidth = svgElement.getAttribute('width') || '800';
      const svgHeight = svgElement.getAttribute('height') || '400';
      const viewBox = svgElement.getAttribute('viewBox');
      
      console.log('🔍 [CLIENT-CAPTURE] SVG element found:', {
        tagName: svgElement.tagName,
        id: svgElement.id,
        className: svgElement.className,
        width: svgWidth,
        height: svgHeight,
        viewBox: viewBox,
        offsetWidth: svgElement.offsetWidth,
        offsetHeight: svgElement.offsetHeight
      });

      // Try capturing the container first (more reliable)
      const container = svgElement.closest('.MuiBox-root') || svgElement.parentElement;
      
      if (container) {
        console.log('🔧 [CLIENT-CAPTURE] Capturing container instead of SVG directly');
        
        const canvas = await html2canvas(container, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          foreignObjectRendering: true,
          removeContainer: false,
          width: parseInt(svgWidth) || 800,
          height: parseInt(svgHeight) || 400
        });
        
        console.log('🔧 [CLIENT-CAPTURE] Container captured successfully');
        
        // Convert to blob
        const blob = await new Promise(resolve => {
          canvas.toBlob(resolve, 'image/png', 0.95);
        });

        console.log('✅ [CLIENT-CAPTURE] Client-side image captured successfully');
        return blob;
      } else {
        // Fallback to direct SVG capture
        console.log('🔧 [CLIENT-CAPTURE] No container found, falling back to direct SVG capture');
        
        const canvas = await html2canvas(svgElement, {
          width: parseInt(svgWidth) || 800,
          height: parseInt(svgHeight) || 400,
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          foreignObjectRendering: true,
          removeContainer: false,
          onclone: (clonedDoc) => {
            const clonedSvg = clonedDoc.querySelector('svg');
            if (clonedSvg) {
              // Set explicit dimensions for the cloned SVG
              clonedSvg.setAttribute('width', svgWidth || '800');
              clonedSvg.setAttribute('height', svgHeight || '400');
              
              // Preserve the original viewBox if it exists
              if (viewBox) {
                clonedSvg.setAttribute('viewBox', viewBox);
              } else {
                // Set a default viewBox based on width and height
                clonedSvg.setAttribute('viewBox', `0 0 ${svgWidth || 800} ${svgHeight || 400}`);
              }
              
              // Ensure xmlns is set
              clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
              
              console.log('🔧 [CLIENT-CAPTURE] Cloned SVG prepared with:', {
                width: clonedSvg.getAttribute('width'),
                height: clonedSvg.getAttribute('height'),
                viewBox: clonedSvg.getAttribute('viewBox')
              });
              
              // Remove problematic attributes that cause html2canvas issues
              const mainGroup = clonedSvg.querySelector('g');
              if (mainGroup && mainGroup.getAttribute('transform')) {
                console.log('🔧 [CLIENT-CAPTURE] Removing transform from main group');
                mainGroup.removeAttribute('transform');
              }
              
              // Remove filter attributes
              const elementsWithFilter = clonedSvg.querySelectorAll('[filter]');
              elementsWithFilter.forEach(el => {
                console.log('🔧 [CLIENT-CAPTURE] Removing filter from:', el.tagName);
                el.removeAttribute('filter');
              });
              
              // Remove any interactive elements that might interfere with capture
              const interactiveElements = clonedSvg.querySelectorAll('[style*="cursor"], [onclick]');
              interactiveElements.forEach(el => {
                el.style.cursor = 'default';
                el.removeAttribute('onclick');
              });
            }
          }
        });

        console.log('🔧 [CLIENT-CAPTURE] Direct SVG capture completed');
        
        // Convert to blob
        const blob = await new Promise(resolve => {
          canvas.toBlob(resolve, 'image/png', 0.95);
        });

        console.log('✅ [CLIENT-CAPTURE] Client-side image captured successfully');
        return blob;
      }
    } catch (error) {
      console.error('❌ [CLIENT-CAPTURE] Error capturing client-side image:', error);
      return null;
    }
  }, [actualSvgRef]);

  // Register with the client image capture service
  useEffect(() => {
    const componentId = 'wye-connector-selection';
    
    console.log('🔧 [CLIENT-CAPTURE] Registering capture function for:', componentId);
    console.log('🔧 [CLIENT-CAPTURE] actualSvgRef status:', {
      hasRef: !!actualSvgRef,
      hasCurrent: !!actualSvgRef?.current,
      elementType: actualSvgRef?.current?.tagName,
      elementId: actualSvgRef?.current?.id
    });
    
    // Create a stable capture function that doesn't change on every render
    const stableCaptureFunction = async () => {
      console.log('🎯 [CLIENT-CAPTURE] Capture function called for:', componentId);
      
      if (!actualSvgRef?.current) {
        console.warn('❌ [CLIENT-CAPTURE] No SVG element found for capture');
        return null;
      }
      
      try {
        console.log('🔄 [CLIENT-CAPTURE] Starting client-side image capture...');
        
        // Add a small delay to ensure SVG is fully rendered
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Use the improved capture function
        return await captureClientSideImage();
        
      } catch (error) {
        console.error('❌ [CLIENT-CAPTURE] Error capturing client-side image:', error);
        return null;
      }
    };
    
    clientImageCaptureService.registerCaptureFunction(componentId, stableCaptureFunction);
    
    return () => {
      console.log('🗑️ [CLIENT-CAPTURE] Unregistering capture function for:', componentId);
      clientImageCaptureService.unregisterCaptureFunction(componentId);
    };
  }, [actualSvgRef]); // Add actualSvgRef as dependency to re-register when ref changes

  // Expose capture function to parent component (legacy support)
  useEffect(() => {
    if (onImageCaptureReady) {
      onImageCaptureReady(captureClientSideImage);
    }
  }, [captureClientSideImage, onImageCaptureReady]);

  // REMOVED: Auto-capture logic - now triggered only by global Next button
  // The global Next button in CableBuilder will handle image generation
  
  // Keep the readiness check for the Next button
  useEffect(() => {
    // Check if all required lengths are set (A, C, D)
    const allLengthsSet = ['A', 'C', 'D'].every(id => 
      lengths[id] !== undefined && 
      lengths[id] !== null && 
      lengths[id] !== '' && 
      !isNaN(lengths[id])
    );
    
    // Check if all required length fields have been manually edited (show green checkmarks)
    const allRequiredLengthsEdited = ['A', 'C', 'D'].every(id => 
      editedFields.has(id)
    );
    
    // Check if all connectors are selected (E, F, G)
    const allConnectorsSelected = ['E', 'F', 'G'].every(id => 
      connectors[id]?.id && 
      connectors[id]?.gender &&
      connectors[id]?.configuration
    );
    
    const isReady = allLengthsSet && allRequiredLengthsEdited && allConnectorsSelected;
    
    if (onReady) {
      onReady(isReady);
      
      // Track missing requirements for popup
      if (typeof onMissingRequirements === 'function') {
        const missingRequirements = [];
        
        // Check missing lengths
        ['A', 'C', 'D'].forEach(id => {
          if (!lengths[id] || lengths[id] === undefined || lengths[id] === null || lengths[id] === '' || isNaN(lengths[id])) {
            missingRequirements.push(`Please set a length for ${id}`);
          } else if (!editedFields.has(id)) {
            missingRequirements.push(`Please interact with length field ${id} (click in the field)`);
          }
        });
        
        // Check missing connectors
        ['E', 'F', 'G'].forEach(id => {
          if (!connectors[id]?.id || !connectors[id]?.gender || !connectors[id]?.configuration) {
            missingRequirements.push(`Please select a connector for ${id}`);
          }
        });
        
        onMissingRequirements(missingRequirements);
      }
    }
  }, [lengths, connectors, editedFields, wireType, onReady, onMissingRequirements]);

  // Green check logic - only show when manually edited
  const isEdited = (id) => {
    return editedFields.has(id);
  };

  // Dimension line variables for professional styling
  const leftmostX = Math.min(E.x, F.x, G.x);
  const rightmostX = Math.max(E.x, F.x, G.x);
  const overallLineY = Math.min(F.y, G.y) - 70;
  
  // Midpoint calculations for dimension labels
  const bMid = { x: (E.x + split.x) / 2, y: (E.y + split.y) / 2 - 30 };
  const cMid = { x: (split.x + F.x) / 2, y: (split.y + F.y) / 2 - 20 };
  const dMid = { x: (split.x + G.x) / 2, y: (split.y + G.y) / 2 + 40 };

  // At the top of the WyeConnectorSelection component:
  // const pairs = { 'E': ['F', 'G'], 'F': ['E'], 'G': ['E'] };

  return (
    <Box sx={{ width: '95%', maxWidth: 1200, margin: '0 auto', p: 1 }}>
      {/* Header with Reset button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" component="h2">
          Configure Wye Connectors
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            onClick={handleReset} 
            variant="outlined" 
            sx={{ 
              fontSize: '0.8rem',
              px: 2,
              py: 0.5
            }}
          >
            RESET
          </Button>

        </Box>
      </Box>
      
      {/* Unit toggle and Wire Type selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1">Units:</Typography>
          <ToggleButtonGroup
            value={unit}
            exclusive
            onChange={handleUnitChange}
            aria-label="unit selection"
            size="small"
          >
            <ToggleButton value="in" aria-label="inches">Inches</ToggleButton>
            <ToggleButton value="ft" aria-label="feet">Feet</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ minWidth: 200 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Cable Type:
          </Typography>
          <TextField
            value="PSZ - Zip Cable"
            size="small"
            fullWidth
            variant="outlined"
            disabled={true}
            InputProps={{
              readOnly: true,
              style: { backgroundColor: '#f5f5f5' }
            }}
            helperText="Wye cables always use PSZ wire type"
          />
        </Box>
      </Box>

      {/* Length fields ABOVE SVG */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {lengthLabels.map(({ id, label }) => {
          const edited = isEdited(id);
          const displayVal = unit === 'ft' ? (parseFloat(lengths[id] || 0) / 12).toFixed(2).replace(/\.00$/, '') : lengths[id] || '';
          
          if (id === 'A') {
            return (
              <Grid item xs={6} md={3} key={id}>
                <TextField
                  label="A (Overall)"
                  value={overallInput}
                  onFocus={() => {
                    setEditedFields(prev => new Set([...prev, 'A']));
                    setLastManualEdit('A');
                  }}
                  onChange={e => handleOverallInputChange(e.target.value)}
                  onBlur={commitOverallInput}
                  onKeyDown={e => { if (e.key === 'Enter') commitOverallInput(); }}
                  type="number"
                  inputProps={{ min: 0, step: unit === 'ft' ? 1/12 : 1 }}
                  fullWidth
                  InputProps={{
                    endAdornment: <span style={{ display: 'flex', alignItems: 'center' }}>{unit === 'ft' ? <span style={{ marginLeft: 4 }}>'</span> : <span style={{ marginLeft: 4 }}>&quot;</span>}{edited && <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18, ml: 0.5 }} titleAccess="User set" />}</span>
                  }}
                  helperText={unit === 'ft' ? "Max: 500 feet" : "Max: 6000 inches"}
                />
              </Grid>
            );
          }
          if (id === 'B') {
            return (
              <Grid item xs={6} md={3} key={id}>
                <TextField
                  label={label}
                  value={displayVal}
                  disabled
                  fullWidth
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                      backgroundColor: '#f5f5f5',
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        {unit === 'ft' ? (
                          <span style={{ marginLeft: 4 }}>'</span>
                        ) : (
                          <span style={{ marginLeft: 4 }}>&quot;</span>
                        )}
                        <Tooltip title="Calculated automatically"><WarningIcon color="action" sx={{ fontSize: 18, ml: 0.5 }} /></Tooltip>
                      </span>
                    )
                  }}
                />
              </Grid>
            );
          }
          return (
            <Grid item xs={6} md={3} key={id}>
              <TextField
                label={label}
                value={tempInputs[id] !== undefined ? tempInputs[id] : displayVal}
                onChange={(e) => handleLengthChange(id, e.target.value)}
                onFocus={() => handleLengthFocus(id)}
                onBlur={() => handleLengthBlur(id)}
                onKeyDown={(e) => { if (e.key === 'Enter') commitLengthInput(id); }}
                type="number"
                inputProps={{ 
                  min: 0, 
                  step: unit === 'ft' ? 1/12 : 1 
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {unit === 'ft' ? (
                        <span style={{ marginLeft: 4 }}>'</span>
                      ) : (
                        <span style={{ marginLeft: 4 }}>&quot;</span>
                      )}
                      {edited && <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18, ml: 0.5 }} titleAccess="User set" />}
                    </span>
                  )
                }}
                helperText={['C', 'D'].includes(id) ? (unit === 'ft' ? "Max: 10 feet" : "Max: 120 inches") : (unit === 'ft' ? "Max: 500 feet" : "Max: 6000 inches")}
              />
            </Grid>
          );
        })}
      </Grid>

      {/* SVG with proper viewBox and preserveAspectRatio */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mb: 2,
        width: '100%',
        border: '1px solid #ddd',
        borderRadius: 2,
        cursor: isPanning ? 'grabbing' : 'grab',
        '& svg': {
          height: 'auto'
        }
      }}>
        <svg 
          ref={actualSvgRef}
          id="wye-cable-svg"
          width="800" 
          height="400"
          viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`} 
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          onMouseDown={handleSvgMouseDown}
          onMouseMove={handleSvgMouseMove}
          onMouseUp={handleSvgMouseUp}
          onMouseLeave={handleSvgMouseUp}
          onTouchStart={handleSvgTouchStart}
          onTouchMove={handleSvgTouchMove}
          onTouchEnd={handleSvgTouchEnd}
          style={{
            maxHeight: '100%',
            width: 'auto',
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}
        >
          {/* Shadow filter */}
          <defs>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="2" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.3" />
            </filter>
          </defs>
          
          <g 
            style={groupStyle}
            transform={`translate(${svgTransform.x}, ${svgTransform.y}) scale(${svgTransform.scale})`}
          >
            {/* Main cable - Represents the main cable length */}
            <path 
              d={generateStraightWire(E.x, E.x + mainLenPx, E.y, parseFloat(lengths['B']) || 6)} 
              stroke="#6495c8" 
              strokeWidth="10" 
              fill="none" 
              filter="url(#shadow)"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.9"
            />
            {/* Add a second path with thicker stroke to show cable bulk */}
            <path 
              d={generateStraightWire(E.x, E.x + mainLenPx, E.y, parseFloat(lengths['B']) || 6)} 
              stroke="#6495c8" 
              strokeWidth="8" 
              fill="none" 
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="1"
            />
            
            {/* Cable sheath outline for better visibility */}
            <rect 
              x={E.x} 
              y={E.y - 6} 
              width={mainLenPx} 
              height="12" 
              rx="6" 
              fill="none" 
              stroke="#6495c8" 
              strokeWidth="1" 
              opacity="0" 
            />
            
            {/* Branch cables with lighter colors */}
            <path 
              d={`M ${split.x} ${split.y} L ${F.x} ${F.y}`} 
              stroke="#6495c8" 
              strokeWidth="6" 
              fill="none" 
              filter="url(#shadow)" 
            />
            <path 
              d={`M ${split.x} ${split.y} L ${G.x} ${G.y}`} 
              stroke="#6495c8" 
              strokeWidth="6" 
              fill="none" 
              filter="url(#shadow)" 
            />

            {/* Connector boxes - boxes disappear when connector is selected */}
            {connectorPoints.map(point => {
              const pos = point.id === 'E' ? E : point.id === 'F' ? F : G;
              const color = '#6495c8';
              const connector = connectors[point.id];
              
              return (
                <g key={point.id} onClick={() => handlePointClick(point.id)} style={{ cursor: 'pointer' }}>
                  {connector?.image ? (
                    <image 
                      href={getImageUrlForDisplay(connectorImages[point.id] || connector.image)}
                      x={pos.x - connW / 2} 
                      y={pos.y - connH / 2} 
                      width={connW} 
                      height={connH} 
                      filter="url(#shadow)"
                    />
                  ) : (
                    <rect 
                      x={pos.x - connW / 2} 
                      y={pos.y - connH / 2} 
                      width={connW} 
                      height={connH} 
                      rx="6" 
                      fill={color} 
                      stroke="#fff" 
                      strokeWidth="3" 
                      filter="url(#shadow)"
                    />
                  )}
                  <text 
                    x={pos.x} 
                    y={pos.y + connH / 2 + 20} 
                    textAnchor="middle" 
                    fontSize="14" 
                    fill="#000" 
                    fontWeight="bold"
                  >
                    {point.label}
                  </text>
                </g>
              );
            })}

            {/* Dimension lines and labels - Professional style */}
            <line x1={leftmostX} y1={overallLineY} x2={rightmostX} y2={overallLineY} stroke="#bbb" strokeWidth="2" />
            <line x1={leftmostX} y1={overallLineY-5} x2={leftmostX} y2={overallLineY+5} stroke="#bbb" strokeWidth="2" />
            <line x1={rightmostX} y1={overallLineY-5} x2={rightmostX} y2={overallLineY+5} stroke="#bbb" strokeWidth="2" />
            <rect x={(leftmostX + rightmostX) / 2 - 48} y={overallLineY - 15} width="96" height="30" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={(leftmostX + rightmostX) / 2} y={overallLineY} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500" dominantBaseline="middle">
              {unit === 'ft' ? `A = ${(parseFloat(aLen) / 12).toFixed(2).replace(/\.00$/, '')}'` : `A = ${aLen}"`}
            </text>
            
            {/* Additional dimension lines */}
            <rect x={bMid.x-32} y={bMid.y+20} width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={bMid.x} y={bMid.y+35} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
              {lengths['B'] ? (unit === 'ft' ? `B = ${(parseFloat(lengths['B']) / 12).toFixed(2).replace(/\.00$/, '')}'` : `B = ${lengths['B']}"`) : 'B'}
            </text>
            <rect x={cMid.x-52} y={cMid.y+10} width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={cMid.x-20} y={cMid.y+25} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
              {lengths['C'] ? (unit === 'ft' ? `C = ${(parseFloat(lengths['C']) / 12).toFixed(2).replace(/\.00$/, '')}'` : `C = ${lengths['C']}"`) : 'C'}
            </text>
            <rect x={dMid.x-52} y={dMid.y-50} width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={dMid.x-20} y={dMid.y-35} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
              {lengths['D'] ? (unit === 'ft' ? `D = ${(parseFloat(lengths['D']) / 12).toFixed(2).replace(/\.00$/, '')}'` : `D = ${lengths['D']}"`) : 'D'}
            </text>
          </g>
        </svg>
      </Box>

      {/* Connector summary row BELOW SVG with configuration options */}
      <Grid container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        {connectorPoints.map(({ id, label }) => {
          const connector = connectors[id];
          const isSelected = connector?.type;
          const currentConfig = connector?.configuration || 'standard';
          let connectorLabel = 'Not selected';
          if (connector?.id) {
            // Get the proper description from the compatibility matrix
            const matrixEntry = compatibilityMatrix[connector.id];
            if (matrixEntry?.description) {
              connectorLabel = matrixEntry.description;
            } else if (connector.description) {
              connectorLabel = connector.description;
            } else if (connector.label) {
              connectorLabel = connector.label;
            } else {
              connectorLabel = connector.id;
            }
          }
          
          return (
            <Grid item xs={6} md={4} key={id}>
              <Paper variant="outlined" sx={{ p: 1, cursor: 'pointer', minHeight: 120, display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }} onClick={() => handlePointClick(id)}>
                {/* Clear button - positioned absolutely in top-right corner */}
                {connector?.id && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearConnector(id);
                    }}
                    sx={{ 
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      color: '#d32f2f',
                      backgroundColor: 'rgba(211, 47, 47, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(211, 47, 47, 0.2)',
                        color: '#b71c1c'
                      },
                      width: 24,
                      height: 24,
                      zIndex: 1
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                    {/* Color-coded box for cable type */}
                    <Box sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: '#6495c8',
                      border: '1px solid #fff',
                      borderRadius: '2px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }} />
                    <span role="img" aria-label="wye" style={{ fontSize: '16px' }}>🔗</span>
                    {connector?.image ? (
                      <img src={connectorImages[id] || connector.image} alt={connector.label} style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
                    ) : (
                      <Box sx={{ width: 40, height: 40, border: '2px dashed #ccc', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#999' }}>?</Box>
                    )}
                    {connector?.type && <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18, ml: 0.5 }} titleAccess="Selected" />}
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary" align="center">Connector {label}</Typography>
                  <Typography variant="body2" align="center" sx={{ mt: 0.5 }}>
                    {connectorLabel}
                    {isXLR5Pin(connector) && (
                      <Tooltip title="Only 3 pins wired" arrow>
                        <span style={{ color: '#ff9800', marginLeft: '4px', fontWeight: 'bold' }}>*</span>
                      </Tooltip>
                    )}
                  </Typography>
                </Box>
                
                {/* Configuration Selector - show when connector is selected */}
                {isSelected && (
                  <Box sx={{ mt: 'auto', pt: 1, borderTop: '1px solid', borderColor: 'divider', width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.7rem', textAlign: 'center' }}>
                      Configuration:
                    </Typography>
                    <ToggleButtonGroup
                      value={currentConfig}
                      exclusive
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, newConfig) => {
                        e.stopPropagation();
                        if (newConfig !== null) {
                          handleConfigurationChange(id, newConfig, e);
                        }
                      }}
                      aria-label={`configuration for connector ${label}`}
                      size="small"
                      sx={{ 
                        width: '100%',
                        '& .MuiToggleButton-root': {
                          fontSize: '0.65rem',
                          px: 0.25,
                          py: 0.2,
                          minWidth: 'unset',
                          flex: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          '&.Mui-selected': {
                            backgroundColor: '#e8f5e8',
                            color: '#2e7d32',
                            border: '1px solid #4caf50',
                            '&:hover': {
                              backgroundColor: '#dcedc8'
                            }
                          }
                        }
                      }}
                    >
                      <Tooltip 
                        title={() => {
                          if (!getConfigurationsForConnector(connector).standard) {
                            return "Configuration not available for this connector type";
                          }
                          const compatCheck = checkConfigurationCompatibility(id, 'standard');
                          if (compatCheck?.incompatible) {
                            return `${compatCheck.reason}${compatCheck.details ? ` - ${compatCheck.details}` : ''}`;
                          }
                          return "Standard straight connector";
                        }}
                        arrow
                      >
                        <span style={{ flex: 1 }}>
                          <ToggleButton 
                            value="standard" 
                            aria-label="standard" 
                            disabled={!getConfigurationsForConnector(connector).standard}
                            sx={{ 
                              flex: 1, 
                              minWidth: 0, 
                              px: 0.5,
                              width: '100%',
                              border: checkConfigurationCompatibility(id, 'standard')?.incompatible ? '2px solid #f44336' : '1px solid',
                              borderColor: checkConfigurationCompatibility(id, 'standard')?.incompatible ? '#f44336' : 'divider',
                              '&.Mui-selected': {
                                backgroundColor: '#e8f5e8',
                                color: '#2e7d32',
                                border: checkConfigurationCompatibility(id, 'standard')?.incompatible ? '2px solid #f44336' : '1px solid #4caf50',
                                '&:hover': {
                                  backgroundColor: '#dcedc8'
                                }
                              },
                              '&.Mui-disabled': {
                                color: 'rgba(0, 0, 0, 0.26)',
                                backgroundColor: 'rgba(0, 0, 0, 0.12)'
                              }
                            }}
                          >
                            STD
                          </ToggleButton>
                        </span>
                      </Tooltip>
                      <Tooltip 
                        title={() => {
                          if (!getConfigurationsForConnector(connector)['right-angle']) {
                            return "Configuration not available for this connector type";
                          }
                          const compatCheck = checkConfigurationCompatibility(id, 'right-angle');
                          if (compatCheck?.incompatible) {
                            return `${compatCheck.reason}${compatCheck.details ? ` - ${compatCheck.details}` : ''}`;
                          }
                          return "Right Angle (90°) connector";
                        }}
                        arrow
                      >
                        <span style={{ flex: 1 }}>
                          <ToggleButton 
                            value="right-angle" 
                            aria-label="right angle" 
                            disabled={!getConfigurationsForConnector(connector)['right-angle']}
                            sx={{ 
                              flex: 1, 
                              minWidth: 0, 
                              px: 0.5,
                              width: '100%',
                              border: checkConfigurationCompatibility(id, 'right-angle')?.incompatible ? '2px solid #f44336' : '1px solid',
                              borderColor: checkConfigurationCompatibility(id, 'right-angle')?.incompatible ? '#f44336' : 'divider',
                              '&.Mui-selected': {
                                backgroundColor: '#e8f5e8',
                                color: '#2e7d32',
                                border: checkConfigurationCompatibility(id, 'right-angle')?.incompatible ? '2px solid #f44336' : '1px solid #4caf50',
                                '&:hover': {
                                  backgroundColor: '#dcedc8'
                                }
                              },
                              '&.Mui-disabled': {
                                color: 'rgba(0, 0, 0, 0.26)',
                                backgroundColor: 'rgba(0, 0, 0, 0.12)'
                              }
                            }}
                          >
                            RA
                          </ToggleButton>
                        </span>
                      </Tooltip>
                      <Tooltip 
                        title={() => {
                          if (!getConfigurationsForConnector(connector)['panel-mount']) {
                            return "Configuration not available for this connector type";
                          }
                          const compatCheck = checkConfigurationCompatibility(id, 'panel-mount');
                          if (compatCheck?.incompatible) {
                            return `${compatCheck.reason}${compatCheck.details ? ` - ${compatCheck.details}` : ''}`;
                          }
                          return "Panel mount connector";
                        }}
                        arrow
                      >
                        <span style={{ flex: 1 }}>
                          <ToggleButton 
                            value="panel-mount" 
                            aria-label="panel mount" 
                            disabled={!getConfigurationsForConnector(connector)['panel-mount']}
                            sx={{ 
                              flex: 1, 
                              minWidth: 0, 
                              px: 0.5,
                              width: '100%',
                              border: checkConfigurationCompatibility(id, 'panel-mount')?.incompatible ? '2px solid #f44336' : '1px solid',
                              borderColor: checkConfigurationCompatibility(id, 'panel-mount')?.incompatible ? '#f44336' : 'divider',
                              '&.Mui-selected': {
                                backgroundColor: '#e8f5e8',
                                color: '#2e7d32',
                                border: checkConfigurationCompatibility(id, 'panel-mount')?.incompatible ? '2px solid #f44336' : '1px solid #4caf50',
                                '&:hover': {
                                  backgroundColor: '#dcedc8'
                                }
                              },
                              '&.Mui-disabled': {
                                color: 'rgba(0, 0, 0, 0.26)',
                                backgroundColor: 'rgba(0, 0, 0, 0.12)'
                              }
                            }}
                          >
                            PNL
                          </ToggleButton>
                        </span>
                      </Tooltip>
                    </ToggleButtonGroup>
                  </Box>
                )}
                {/* Removed: {!compatibilityResults[connector?.id]?.compatible && (
                  <CompatibilityWarning compatibilityResult={compatibilityResults[connector?.id]} connectorA={connector} connectorB={null} />
                )} */}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Special Instructions */}
      <TextField
        label="Special Instructions (Optional)"
        multiline
        rows={3}
        fullWidth
        value={specialInstructions}
        onChange={handleInstructionsChange}
        sx={{ mb: 2 }}
        placeholder="How are you going to use this cable? We evaluate every cable we build... Please DO NOT include any personal information!"
      />

      {/* Compatibility Warnings */}
      {Object.keys(compatibilityWarnings).length > 0 && (
        <Box sx={{ mb: 2 }}>
          {Object.entries(compatibilityWarnings).map(([key, warning]) => (
            <Alert 
              key={key}
              severity={warning.type || 'warning'} 
              sx={{ mb: 1 }}
              icon={<WarningIcon />}
            >
              <Typography variant="body2" fontWeight="bold">
                Connector Compatibility Note: {key.replace('-', ' ↔ ')}
              </Typography>
              <Typography variant="body2">
                {warning.message}
              </Typography>
              {warning.details && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {warning.details}
                </Typography>
              )}
            </Alert>
          ))}
        </Box>
      )}

      {/* Modal for connector selection */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="connector-modal-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="connector-modal-title">
          Select Wye Connector for Point {selectedPoint}
        </DialogTitle>
        <DialogContent>
          {selectedPoint && (
            <ConnectorModal 
              selectedPoint={selectedPoint}
              connectors={connectors}
              onConnectorSelect={handleConnectorSelect}
              getFilteredConnectors={getFilteredConnectors}
              type={type}
              connectorFamilies={connectorFamilies}
              findConnectorVariant={findConnectorVariant}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      
      {/* Tooltip for configuration change notifications */}
      <Tooltip
        open={tooltipOpen}
        anchorEl={tooltipAnchor}
        onClose={() => setTooltipOpen(false)}
        title={tooltipMessage}
        arrow
        placement="top"
        sx={{
          '& .MuiTooltip-tooltip': {
            backgroundColor: '#d32f2f',
            color: 'white',
            fontSize: '0.875rem',
            maxWidth: 300
          }
        }}
      />
    </Box>
  );
}

export default WyeConnectorSelection; 